<template>
  <!-- wrapper div helps deal with border tapering issue-->
  <div class="collapse-wrapper" :style="collapseWrapperStyle">
    <div class="collapse-container border" :class="collapseClass">
      <button
        class="d-block w-100 text-decoration-none"
        :class="collapseButtonClasses"
        :aria-expanded="isExpanded"
        :aria-controls="formattedCollapseId"
        :id="`accordion${accordionIndex + collapseId}`"
        @click="handleClick($event)"
        type="button">
        <div
          class="collapse-label-container d-flex justify-content-between align-items-center">
          <div>
            <div
              class="collapse-title d-flex justify-content-between align-items-center">
              <div
                class="d-flex flex-row justify-content-between align-items-center">
                <slot name="step-slot">
                  <div
                    v-if="step"
                    class="me-space-xs number-block"
                    :class="stepClass">
                    <span>{{ step }}</span>
                  </div>
                </slot>

                <slot name="title-slot">
                  <h3 class="text-start" :class="titleClass">
                    {{ title }}
                  </h3>
                </slot>
              </div>
            </div>
          </div>
          <div
            class="collapse-icon d-flex justify-content-between align-items-start ms-space-xs ms-md-space-sm ms-lg-space-md"
            :class="iconClassesComputed">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
              class="icon-size d-inline-block">
              <!--! Font Awesome Pro 6.3.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2023 Fonticons, Inc. -->
              <path
                d="M201.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 338.7 54.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z" />
            </svg>
          </div>
        </div>
      </button>
      <transition
        name="accordion"
        @enter="start"
        @after-enter="end"
        @before-leave="start"
        @after-leave="end">
        <div
          :id="formattedCollapseId"
          class="collapse"
          ref="collapse"
          :class="collapseSectionClass"
          v-show="isExpanded"
          role="region"
          :aria-labelledby="`accordion${accordionIndex + collapseId}`">
          <div
            class="collapse-content ps-space-xs pe-space-lg ps-md-space-sm pe-md-space-lg ps-lg-space-md pe-lg-space-xl py-space-xs pb-md-space-sm border-top"
            :class="`border-${borderVariant}`">
            <slot></slot>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref } from "vue";

interface Props {
  collapseId: string;
  startExpanded?: boolean;
  step?: string | number;
  stepVariant?: string;
  stepBackgroundVariant?: string;
  title?: string;
  titleSize?: "xl" | "large" | "small" | "medium";
  titleVariant?: string;
  titleBackgroundVariant?: string;
  displayHighlight?: boolean;
  highlightColor?: string;
  highlightWidth?: number;
  highlightLocation?: "top" | "left" | "right" | "bottom";
  iconVariant?: string;
  iconClasses?: string;
  contentBackgroundVariant?: string;
  borderVariant?: string;
  buttonSize?: "sm" | "md" | "custom";
  buttonClasses?: string;
}

const props = withDefaults(defineProps<Props>(), {
  collapseId: "1",
  startExpanded: false,
  stepVariant: "dark-3",
  stepBackgroundVariant: "secondary",
  contentBackgroundVariant: "light-1",
  title: "Lorem Ipsum",
  titleSize: "small",
  titleVariant: "dark-3",
  titleBackgroundVariant: "white",
  displayHighlight: true,
  highlightColor: "--rds-secondary",
  highlightWidth: 8,
  highlightLocation: "left",
  iconVariant: "dark-3",
  borderVariant: "light-4",
  buttonSize: "md",
});

/** Refs */
// Ref to collapse element
const collapse = ref<HTMLInputElement | null>(null);

const initialLoad = ref(false);
const accordionIndex = ref(0);

interface Accordion {
  count: number;
  active: number | null;
  start_collapsed: boolean;
}

// fall back ref if not using accordion parent component
const defaultAccordion = ref<Accordion>({
  count: 0,
  active: props.startExpanded ? 0 : null,
  start_collapsed: true,
});

const accordion = inject<Accordion>(
  "Accordion-tracker",
  defaultAccordion.value
);

/** Events */
const emits = defineEmits<{
  /**
   *  Emitted when the collapse finishes hidding.
   */
  (e: "collapseHidden", el: HTMLElement): void;
  /**
   *  Emitted when the collapse finishes opening.
   */
  (e: "collapseShown", el: HTMLElement): void;
  /**
   *  Emitted when any collapse button is interacted with.
   */
  (e: "collapseInput", event: Event): void;
}>();

const isExpanded = computed(() => {
  return accordionIndex.value == accordion.active;
});

const formattedCollapseId = computed(() => {
  return `collapse-${props.collapseId}`;
});

const alignMap = {
  left: "start",
  center: "center",
  right: "end",
  top: "top",
  bottom: "bottom",
};

const collapseWrapperStyle = computed(() => {
  return props.displayHighlight
    ? `border-${props.highlightLocation}: ${
        props.highlightWidth
      }px solid ${getColor(props.highlightColor)} !important;`
    : "";
});

const collapseSectionClass = computed(() => {
  let classes = `bg-${props.contentBackgroundVariant} `;

  isExpanded.value ? (classes += " show") : null;

  return classes;
});

const stepClass = computed(() => {
  let result = `text-${props.stepVariant} bg-${props.stepBackgroundVariant}`;

  if (props.step !== undefined) {
    let formattedStep = props.step.toString();

    if (formattedStep.length > 1) {
      result += " double-digit";
    } else {
      result += " single-digit";
    }
  }

  return result;
});

const titleClass = computed(() => {
  return `h3-${props.titleSize} text-${props.titleVariant}`;
});

const iconClassesComputed = computed(() => {
  let classes = ` text-${props.iconVariant} `;

  props.buttonSize == "sm" ? (classes += " sm-btn ") : null;
  isExpanded.value === true ? (classes += " not-collapsed ") : null;

  props.iconClasses ? (classes += props.iconClasses) : null;
  return classes;
});

const collapseClass = computed(() => {
  let classes = `border-${props.borderVariant} bg-${props.titleBackgroundVariant} `;

  props.displayHighlight
    ? (classes += `border-${alignMap[props.highlightLocation]}-0 `)
    : null;

  return classes;
});

const collapseButtonClasses = computed(() => {
  let classes = "";
  if (props.buttonSize == "sm") {
    classes += ` p-space-xs `;
  } else if (props.buttonSize == "md") {
    classes += ` px-space-xs py-space-xs px-md-space-sm px-lg-space-md py-md-space-sm `;
  }
  props.buttonClasses ? (classes += props.buttonClasses) : null;
  return classes;
});

const getColor = (color: string) => {
  if (color.includes("var(")) {
    return `${color}`;
  } else if (color.includes("--")) {
    return `var(${color})`;
  } else {
    return color;
  }
};

/** Methods */
const toggleCollapse = () => {
  isExpanded.value
    ? (accordion.active = null)
    : (accordion.active = accordionIndex.value);
};

// handle clicks
const handleClick = (e: Event) => {
  emits("collapseInput", e);
  toggleCollapse();
};

// collapse transition inspired by https://github.com/danielmalmros vue accordion
const start = (el: HTMLElement | Element) => {
  if (el instanceof HTMLElement) {
    // expand b
    if (isExpanded.value) {
      el.style.height = "0px";
      el.style.height = el.scrollHeight + "px";
    } else if (initialLoad.value) {
      el.style.height = el.scrollHeight + "px";
      setTimeout(() => {
        el.style.height = "0px";
      }, 10);
    }
  }
};

const end = (el: HTMLElement | Element) => {
  if (el instanceof HTMLElement) {
    el.style.height = "";
    if (initialLoad.value === true) {
      isExpanded.value
        ? emits("collapseShown", el)
        : emits("collapseHidden", el);
    } else {
      initialLoad.value = true;
    }
  }
};

onMounted(() => {
  accordionIndex.value = accordion.count++;
  // logic to set initialLoad to true if accordion isn't used or accordion is set to start out collapse
  isExpanded.value ? (initialLoad.value = true) : null;
  accordion.start_collapsed ? (initialLoad.value = true) : null;
});

onUnmounted(() => {
  accordion.count--;
});
</script>

<style lang="scss" scoped>
.collapse-container button {
  overflow-anchor: none;
  border: none;
  background: inherit;
  border-radius: 0;
  width: 100%;
}
.collapse-icon {
  flex-shrink: 0;
  width: 1rem;
  transition: transform 0.2s;

  & svg {
    height: 100%;
    width: 100%;
    overflow: visible;
    vertical-align: -0.125em;
    fill: currentColor;
  }
  &.not-collapsed {
    transform: rotate(180deg);
  }
}

.collapse {
  height: auto;
  overflow: hidden;
  width: 100%;
  // overriding bootstrap, might remove later
  &:not(.show) {
    display: block;
  }
}
.number-block {
  display: inline-block;
  font-size: 16px;
  border-radius: 50%;
  text-align: center;
  font-weight: bold;
  line-height: 1.4;
  &.single-digit {
    padding: 4px 10.5px;
  }
  &.double-digit {
    padding: 4.5px 7px;
  }
}

.accordion-enter-active,
.accordion-leave-active {
  will-change: height, opacity;
  transition: height 0.2s ease;
  overflow: hidden;
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .collapse-icon {
    width: 2rem;
  }
  .collapse-icon.sm-btn {
    width: 1.75rem;
  }
}
</style>
